// Web Storage access that never throws. Storage can be unavailable (blocked cookies, private
// windows, quota errors), and a remembered preference is never worth crashing a page over.

import { logger } from "@/lib/logger";

/** localStorage outlives the tab; sessionStorage is per tab and survives reloads. */
type StorageArea = "local" | "session";

function getStorage(area: StorageArea): Storage | null {
    if (typeof window === "undefined") {
        return null;
    }

    return area === "session" ? window.sessionStorage : window.localStorage;
}

/** The stored value, or null when storage is unavailable or the key is unset. */
export function readStorage(key: string, area: StorageArea = "local"): string | null {
    try {
        return getStorage(area)?.getItem(key) ?? null;
    } catch (err) {
        logger.warn(`Failed to read from ${area}Storage`, err, { "contentvault.storage.key": key });
        return null;
    }
}

/** Stores a value, ignoring failures: the preference simply won't outlive the page. */
export function writeStorage(key: string, value: string, area: StorageArea = "local"): void {
    try {
        getStorage(area)?.setItem(key, value);
    } catch (err) {
        logger.warn(`Failed to write to ${area}Storage`, err, { "contentvault.storage.key": key });
    }
}

/** Removes a value, ignoring failures. */
export function removeStorage(key: string, area: StorageArea = "local"): void {
    try {
        getStorage(area)?.removeItem(key);
    } catch (err) {
        logger.warn(`Failed to remove from ${area}Storage`, err, { "contentvault.storage.key": key });
    }
}
